/**
 * Light, dark, or whatever the device says.
 *
 * `tokens.css` has carried a full dark palette since the restyle, under
 * `:root[data-theme='dark']`, and nothing ever set that attribute — so the dark
 * half of the design was unreachable. This sets it.
 *
 * Three states, not two. "System" is the default and follows the device, which
 * is what a tablet on a schedule does at dusk without anybody deciding; light
 * and dark are a person overriding that for this device.
 *
 * System is resolved here rather than in a media query on purpose. The palette
 * lives in exactly one rule — shared with `[data-room='pass']`, which is dark
 * whatever the device says — and a `prefers-color-scheme` block would be a
 * second copy of thirty tokens for the two to drift apart over. The cost is
 * that the attribute is written by script, so `startTheme()` runs before the
 * first render.
 *
 * Remembered per device rather than per account, for the same reason the till
 * remembers which till it is: the screen bolted to the pass wants the same
 * answer whoever signs in, and the office laptop wants its own.
 */

import { useCallback, useSyncExternalStore } from 'react';

export type ThemeChoice = 'system' | 'light' | 'dark';

const KEY = 'ui.theme';
const listeners = new Set<() => void>();
let watching = false;

function read(): ThemeChoice {
  try {
    const stored = localStorage.getItem(KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function systemPrefersDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function apply(choice: ThemeChoice): void {
  const dark = choice === 'dark' || (choice === 'system' && systemPrefersDark());
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

/**
 * Applied before React renders, so a dark device does not flash the light
 * ground while the bundle boots. Also starts following the device, so a tablet
 * left on overnight changes with it rather than at the next reload.
 */
export function startTheme(): void {
  apply(read());
  if (watching) return;
  watching = true;
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (read() === 'system') apply('system');
  });
}

export function useTheme(): { choice: ThemeChoice; choose: (next: ThemeChoice) => void } {
  const choice = useSyncExternalStore(subscribe, read, () => 'system' as ThemeChoice);

  const choose = useCallback((next: ThemeChoice) => {
    try {
      if (next === 'system') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, next);
    } catch {
      // A browser refusing storage still gets the change for this session.
    }
    apply(next);
    for (const listener of listeners) listener();
  }, []);

  return { choice, choose };
}
